import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminPayouts() {
  const [tab, setTab] = useState("payouts");
  const [kyc, setKyc] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const load = useCallback(() => {
    adminApi.get("/admin/kyc?status=pending").then(({ data }) => setKyc(data.kyc_requests)).catch(() => {});
    adminApi.get("/admin/payouts").then(({ data }) => setPayouts(data.payouts)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const kycAction = async (u, action) => {
    try {
      await adminApi.post(`/admin/kyc/${u.user.id}/${action}`, { note: action === "reject" ? window.prompt("Rejection reason:") || "Documents unclear" : "" });
      toast.success(`KYC ${action}ed`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const payoutAction = async (p, action) => {
    try {
      await adminApi.post(`/admin/payouts/${p.id}/${action}`, { note: action === "reject" ? window.prompt("Rejection reason:") || "Rejected" : "" });
      toast.success(action === "approve" ? "Payout marked paid (mock transfer)" : "Payout rejected — rubies refunded");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div data-testid="admin-payouts">
      <h1 className="text-xl font-extrabold font-display mb-6">Payouts & KYC</h1>
      <div className="flex gap-2 mb-5">
        {[["payouts", `Payout Queue (${payouts.filter((p) => p.status === "pending").length})`], ["kyc", `KYC Queue (${kyc.length})`]].map(([k, label]) => (
          <button key={k} data-testid={`payouts-tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs font-bold px-4 py-2 rounded-full ${tab === k ? "gradient-primary text-white" : "bg-white/5 text-zinc-400"}`}>{label}</button>
        ))}
      </div>

      {tab === "kyc" && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="p-3">User</th><th>Legal name</th><th>PAN</th><th>Payout to</th><th>Actions</th></tr></thead>
            <tbody>
              {kyc.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-600">No pending KYC requests</td></tr>}
              {kyc.map((r) => (
                <tr key={r.user.id} className="border-b border-white/5" data-testid={`kyc-row-${r.user.id}`}>
                  <td className="p-3 flex items-center gap-2"><img src={r.user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" /><span className="text-white font-semibold">{r.user.name}</span></td>
                  <td className="text-zinc-300">{r.kyc.full_name}</td>
                  <td className="text-zinc-400 font-mono">{r.kyc.pan}</td>
                  <td className="text-zinc-400">{r.kyc.upi || `${r.kyc.bank_account} / ${r.kyc.ifsc}`}</td>
                  <td className="pr-3 space-x-1.5 whitespace-nowrap">
                    <button data-testid={`kyc-verify-${r.user.id}`} onClick={() => kycAction(r, "verify")} className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-lg font-semibold">Verify</button>
                    <button data-testid={`kyc-reject-${r.user.id}`} onClick={() => kycAction(r, "reject")} className="bg-rose-500/20 text-rose-400 px-3 py-1 rounded-lg font-semibold">Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payouts" && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="p-3">Streamer</th><th>Rubies</th><th>Gross</th><th>Fee</th><th>Net payable</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {payouts.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-600">No payout requests</td></tr>}
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-white/5" data-testid={`payout-row-${p.id}`}>
                  <td className="p-3 flex items-center gap-2"><img src={p.user?.avatar} alt="" className="w-7 h-7 rounded-full object-cover" /><span className="text-white font-semibold">{p.user?.name}</span></td>
                  <td className="text-cyan-300 font-bold">{p.diamonds.toLocaleString()}</td>
                  <td className="text-zinc-300">₹{p.gross_inr.toLocaleString()}</td>
                  <td className="text-zinc-500">₹{p.commission_inr.toLocaleString()}</td>
                  <td className="text-emerald-400 font-bold">₹{p.net_inr.toLocaleString()}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-400" : p.status === "rejected" ? "bg-rose-500/20 text-rose-400" : "bg-amber-500/20 text-amber-400"}`}>{p.status}</span></td>
                  <td className="pr-3 space-x-1.5 whitespace-nowrap">
                    {p.status === "pending" && (
                      <>
                        <button data-testid={`payout-approve-${p.id}`} onClick={() => payoutAction(p, "approve")} className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-lg font-semibold">Mark Paid</button>
                        <button data-testid={`payout-reject-${p.id}`} onClick={() => payoutAction(p, "reject")} className="bg-rose-500/20 text-rose-400 px-3 py-1 rounded-lg font-semibold">Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
